/**
 * MemberDetailModal — drill-in dialog opened from a Welcome starter card.
 *
 * Per BP-028 spec §7. Shows a larger avatar + identity row, the personality
 * quote, the "what they do" description, and 3 recommended Jobs the user
 * can launch with one click (each opens the Run-a-Job flow pre-filled with
 * `(member, template)`). Footer offers Chat (primary) and Show-full-profile
 * (ghost).
 *
 * The Modal primitive owns close behavior (× button, Esc, backdrop click)
 * via Radix Dialog — we only forward `onOpenChange(false)` to our `onClose`.
 */

import { useNavigate } from 'react-router-dom';
import { Modal } from '@/components/Modal/Modal';
import { Button } from '@/components/Button/Button';
import { Avatar } from '@/components/Avatar/Avatar';
import { Chip } from '@/components/Chip/Chip';
import { t } from '@/lib/i18n';
import type { Member } from '@/features/members/schemas';
import { useRecommendedJobs } from '../hooks';
import styles from './MemberDetailModal.module.css';

interface Props {
  member: Member;
  onClose: () => void;
  onRunJob: (templateId: string, memberId: string) => void;
}

export function MemberDetailModal({ member, onClose, onRunJob }: Props) {
  const navigate = useNavigate();
  const { data } = useRecommendedJobs(member.id);
  const jobs = data?.items ?? [];

  const fullName = [member.first_name, member.last_name].filter(Boolean).join(' ');
  const positionName = member.positions[0]?.name ?? '';
  const departmentName = member.units[0]?.name ?? '';
  const description = member.description ?? '';
  const quote = member.personality ?? '';

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  return (
    <Modal
      open
      onOpenChange={handleOpenChange}
      title={fullName}
      size="lg"
      actions={
        <>
          <Button variant="ghost" onClick={() => void navigate(`/team/m/${member.id}`)}>
            {t('Show full profile')}
          </Button>
          <Button variant="primary" onClick={() => void navigate(`/team/m/${member.id}/chat`)}>
            {t('Chat with {name} →', { name: member.first_name })}
          </Button>
        </>
      }
    >
      <div className={styles.head}>
        <Avatar name={fullName} kind="ai" size="lg" />
        <div className={styles.headText}>
          <h2 className={styles.name}>{fullName}</h2>
          <div className={styles.tags}>
            {positionName ? (
              <Chip variant="neutral" size="sm">
                {positionName}
              </Chip>
            ) : null}
            {departmentName ? (
              <Chip variant="neutral" size="sm">
                {departmentName}
              </Chip>
            ) : null}
          </div>
        </div>
      </div>

      {quote ? <blockquote className={styles.quote}>&ldquo;{quote}&rdquo;</blockquote> : null}
      {description ? <p className={styles.desc}>{description}</p> : null}

      {jobs.length > 0 ? (
        <>
          <p className={styles.sectionLabel}>
            {t('Try {name} on one of these first', { name: member.first_name })}
          </p>
          <div className={styles.jobs}>
            {jobs.slice(0, 3).map((j) => (
              <button
                type="button"
                key={j.id}
                className={styles.job}
                onClick={() => onRunJob(j.id, member.id)}
              >
                <span className={styles.jobTitle}>{j.title}</span>
                {j.description ? <span className={styles.jobDesc}>{j.description}</span> : null}
                <span className={styles.arrow} aria-hidden="true">
                  →
                </span>
              </button>
            ))}
          </div>
        </>
      ) : null}
    </Modal>
  );
}
